import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Star } from "lucide-react";
import api from "@/lib/api";

const track = (rail, action, product_id) => {
  api.post("/marketplace/reco/event", { rail, action, product_id }).catch(() => {});
};

function RecoCard({ p, rail, nav, wide }) {
  const d = p.mrp && p.price && p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : (p.discount || 0);
  const img = p.thumb || (p.images || [])[0] || p.image;
  const go = () => { track(rail, "click", p.product_id); nav(`/product/${p.slug || p.product_id}`); };
  return (
    <div onClick={go} data-testid={`reco-${rail}-${p.product_id}`}
      className={`${wide ? "" : "w-40 shrink-0"} cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:shadow-md`}>
      <div className="aspect-square bg-slate-100">
        {img ? <img src={img} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
          : <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>}
      </div>
      <div className="p-3">
        {p.brand && <p className="truncate text-[10px] uppercase text-slate-400">{p.brand}</p>}
        <p className="line-clamp-2 text-sm font-medium">{p.name}</p>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="font-bold">₹{Math.round(p.price || 0)}</span>
          {d > 0 && <span className="text-xs font-semibold text-emerald-600">{d}% OFF</span>}
        </div>
        {(p.rating_avg || p.rating) ? (
          <p className="mt-1 flex items-center gap-1 text-xs text-amber-500">
            <Star className="h-3 w-3 fill-amber-400" /> {p.rating_avg || p.rating}{p.rating_count ? ` (${p.rating_count})` : ""}
          </p>) : null}
      </div>
    </div>
  );
}

/**
 * Centralized recommendation rail. Fetches from the ONE recommendation engine, hides itself
 * when there are no REAL products, and records impression/click analytics.
 * @param {string} path - full api path (parent injects serviceability loc query)
 * @param {string} rail - rail id for analytics + testids
 * @param {"scroll"|"grid"} layout
 */
export default function RecoRail({ path, rail, title, subtitle, layout = "scroll", limit }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const impressed = useRef(false);

  useEffect(() => {
    let alive = true;
    api.get(path).then((r) => { if (alive) { setItems(r.data.products || []); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [path]);

  useEffect(() => { impressed.current = false; }, [path]);

  useEffect(() => {
    if (items.length && !impressed.current) { impressed.current = true; track(rail, "impression"); }
  }, [items, rail]);

  if (!loaded || items.length === 0) return null;
  const shown = limit ? items.slice(0, limit) : items;

  return (
    <section data-testid={`reco-rail-${rail}`} className="mt-8">
      <div className="mb-3">
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {layout === "grid" ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {shown.map((p) => <RecoCard key={p.product_id} p={p} rail={rail} nav={nav} wide />)}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {shown.map((p) => <RecoCard key={p.product_id} p={p} rail={rail} nav={nav} />)}
        </div>
      )}
    </section>
  );
}
